import React, { useRef, useState } from 'react';
import {
  Avatar, Badge, Box, CircularProgress, IconButton, Tooltip, Typography,
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';
import { resolveUserAvatarUrl } from '../../constants/userAvatars';

export interface UserAvatarUploadProps {
  userId?: string;
  fullName: string;
  avatarUrl?: string | null;
  gender?: string | null;
  size?: number;
  editable?: boolean;
  /** Rota de upload. Padrão: perfil do usuário logado. */
  uploadPath?: string;
  /** Rota para remover foto. Padrão: DELETE /auth/profile/avatar */
  deletePath?: string;
  onAvatarUpdated?: (avatarUrl: string | null) => void;
}

const UserAvatarUpload: React.FC<UserAvatarUploadProps> = ({
  fullName,
  avatarUrl,
  gender,
  size = 88,
  editable = true,
  uploadPath = '/auth/profile/avatar',
  deletePath = '/auth/profile/avatar',
  onAvatarUpdated,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [localUrl, setLocalUrl] = useState<string | null | undefined>(avatarUrl);

  React.useEffect(() => {
    setLocalUrl(avatarUrl);
  }, [avatarUrl]);

  const displaySrc = resolveUserAvatarUrl({ avatar_url: localUrl, gender });
  const initials = fullName
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const uploadFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      window.alert('Selecione apenas arquivos de imagem.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      window.alert('A imagem deve ter no máximo 10MB.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${getApiUrl()}${uploadPath}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (!data?.success) throw new Error(data?.error || 'Erro ao enviar foto');
      const nextUrl = data.data?.avatar_url || null;
      setLocalUrl(nextUrl);
      onAvatarUpdated?.(nextUrl);
    } catch (err: any) {
      window.alert(err.response?.data?.error || err.message || 'Erro ao enviar foto');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remover a foto de perfil?')) return;
    setUploading(true);
    try {
      await axios.delete(`${getApiUrl()}${deletePath}`);
      setLocalUrl(null);
      onAvatarUpdated?.(null);
    } catch (err: any) {
      window.alert(err.response?.data?.error || 'Erro ao remover foto');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        badgeContent={
          editable ? (
            <Tooltip title="Alterar foto">
              <IconButton
                size="small"
                disabled={uploading}
                onClick={() => inputRef.current?.click()}
                sx={{
                  bgcolor: '#0d9488',
                  color: '#fff',
                  width: 30,
                  height: 30,
                  border: '2px solid #fff',
                  '&:hover': { bgcolor: '#0f766e' },
                }}
              >
                {uploading ? <CircularProgress size={14} color="inherit" /> : <CameraIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
          ) : null
        }
      >
        <Avatar
          src={displaySrc || undefined}
          sx={{
            width: size,
            height: size,
            fontSize: size * 0.32,
            fontWeight: 700,
            bgcolor: '#0d9488',
          }}
        >
          {!displaySrc && initials}
        </Avatar>
      </Badge>

      {editable && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void uploadFile(file);
            }}
          />
          {localUrl && (
            <Typography
              component="button"
              type="button"
              onClick={() => void handleRemove()}
              disabled={uploading}
              sx={{
                border: 'none',
                background: 'none',
                color: '#ef4444',
                fontSize: '0.75rem',
                cursor: uploading ? 'default' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                p: 0,
              }}
            >
              <DeleteIcon sx={{ fontSize: 14 }} />
              Remover foto
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default UserAvatarUpload;
